import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';

import { saveSalesforceCredentials } from '../api/credentials';
import { SalesforceSetupCarousel } from './SalesforceSetupCarousel';

type SalesforceCredentialsMode = 'setup' | 'replace';

interface SalesforceCredentialsFormProps {
  orgId: number | string;
  /** 'setup' for a first-time connected app, 'replace' to swap an existing pair. */
  mode?: SalesforceCredentialsMode;
  /**
   * The authoritative OAuth callback URL from the backend status endpoint (built
   * from `settings.SITE_URL` / the local-proxy host). When omitted, the form falls
   * back to deriving one from the browser origin so callers without the value
   * still render something usable.
   */
  callbackUrl?: string;
  /** Rendered as a Cancel action. Required in practice for 'replace'. */
  onCancel?: () => void;
}

const CARD_CLASS =
  'max-w-2xl rounded-xl border border-[var(--color-border-secondary)] p-6';

// Salesforce requires the connected app's callback URL to match the exact absolute
// URI the OAuth flow redirects back to. The backend builds that redirect_uri from
// `settings.SITE_URL` (or the local-proxy host) and returns it as `callback_url` on
// the status endpoint, so the `callbackUrl` prop is authoritative. This path is only
// the fallback tail used when the prop is absent — note the SPA origin can differ
// from the API host, which is exactly what causes a redirect_uri_mismatch.
const CALLBACK_PATH = '/accounts/salesforce/callback/';

const SETUP_STEPS = [
  'In Salesforce, go to Setup → App Manager → New External Client App.',
  'Enable OAuth and add these scopes: Manage user data via APIs (api), Perform requests at any time (refresh_token), and Access the identity URL service (id).',
  'Set the Callback URL to the exact value shown below the steps, then save.',
  'Copy the Consumer Key and Consumer Secret from the app and paste them below.',
];

const STEP_CLASS =
  'text-body-small text-[var(--color-text-secondary)] list-decimal';

const COPY: Record<
  SalesforceCredentialsMode,
  { heading: string; intro: string; submit: string; submitting: string }
> = {
  setup: {
    heading: 'Set up your Salesforce connected app',
    intro:
      'Salesforce now requires each org to register its own External Client App. Create one in Salesforce, then paste its credentials here.',
    submit: 'Save credentials',
    submitting: 'Saving…',
  },
  replace: {
    heading: 'Replace your Salesforce credentials',
    // Deliberately states the stored secret is unreadable: it lives encrypted in
    // apps.secrets and no endpoint returns it, so "re-enter both" is the only path.
    intro:
      'Paste the Consumer Key and Consumer Secret from your Salesforce External Client App. The stored secret can’t be shown, so enter both values – saving replaces the existing pair.',
    submit: 'Replace credentials',
    submitting: 'Replacing…',
  },
};

export function SalesforceCredentialsForm({
  orgId,
  mode = 'setup',
  callbackUrl,
  onCancel,
}: SalesforceCredentialsFormProps) {
  const queryClient = useQueryClient();
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the typed-in credentials when the admin switches orgs. This form is not
  // remounted across org switches, so without this the previous org's Consumer Key /
  // Secret would carry over and could be saved against the new org.
  useEffect(() => {
    setClientId('');
    setClientSecret('');
    setIsSaving(false);
    setError(null);
  }, [orgId]);

  const copy = COPY[mode];

  // Absolute callback URL to display. Prefer the backend-derived value (the
  // authoritative OAuth redirect_uri built from SITE_URL / the local-proxy host) so
  // the admin pastes the exact URI Salesforce requires. Fall back to the browser
  // origin plus the fixed path only when the prop is absent, since the SPA origin can
  // differ from the API host and produce a redirect_uri_mismatch.
  const resolvedCallbackUrl =
    callbackUrl ??
    (typeof window !== 'undefined'
      ? `${window.location.origin}${CALLBACK_PATH}`
      : CALLBACK_PATH);

  const handleClientIdChange = useCallback((value: string) => {
    setClientId(value);
    setError(null);
  }, []);

  const handleClientSecretChange = useCallback((value: string) => {
    setClientSecret(value);
    setError(null);
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    try {
      await saveSalesforceCredentials(orgId, clientId, clientSecret);
      // Refetch status so `credentials_configured` flips and the Connect card
      // replaces this form.
      await queryClient.invalidateQueries({
        queryKey: ['salesforceStatus', orgId],
      });
      onCancel?.();
    } catch (e) {
      console.error(e);
      setError("We couldn't save your Salesforce credentials. Try again.");
      setIsSaving(false);
    }
  }, [orgId, clientId, clientSecret, queryClient, onCancel]);

  const canSave =
    clientId.trim().length > 0 && clientSecret.trim().length > 0 && !isSaving;

  return (
    <div className={CARD_CLASS}>
      <div className='space-y-1'>
        <h3 className='text-heading-3 text-[var(--color-text-primary)]'>
          {copy.heading}
        </h3>
        <p className='text-body-small text-[var(--color-text-secondary)]'>
          {copy.intro}
        </p>
      </div>

      {mode === 'setup' && (
        <>
          <ol className='mt-4 ml-4 space-y-1.5'>
            {SETUP_STEPS.map(step => (
              <li key={step} className={STEP_CLASS}>
                {step}
              </li>
            ))}
          </ol>
          <div className='mt-3'>
            <p className='text-body-small text-[var(--color-text-secondary)]'>
              Callback URL
            </p>
            <code className='mt-1 block w-full overflow-x-auto rounded-md border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] px-3 py-2 text-body-small text-[var(--color-text-primary)]'>
              {resolvedCallbackUrl}
            </code>
          </div>
          <div className='mt-4'>
            <p className='mb-2 text-body-small text-[var(--color-text-secondary)]'>
              Each step, shown in Salesforce (click a screen to enlarge):
            </p>
            <SalesforceSetupCarousel />
          </div>
        </>
      )}

      <div className='mt-5 space-y-3'>
        <Input
          label='Consumer Key'
          type='text'
          value={clientId}
          onChange={handleClientIdChange}
          placeholder='e.g. 3MVG9'
          aria-label='Salesforce consumer key'
        />
        <Input
          label='Consumer Secret'
          type='password'
          value={clientSecret}
          onChange={handleClientSecretChange}
          placeholder='Paste the consumer secret'
          aria-label='Salesforce consumer secret'
        />
      </div>

      <div className='mt-6 flex items-center gap-3'>
        <Button
          color='primary'
          size='md'
          onClick={handleSave}
          isDisabled={!canSave}
          isLoading={isSaving}
          showTextWhileLoading
        >
          {isSaving ? copy.submitting : copy.submit}
        </Button>
        {onCancel && (
          <Button
            color='secondary'
            size='md'
            onClick={onCancel}
            isDisabled={isSaving}
          >
            Cancel
          </Button>
        )}
      </div>

      {error && <p className='mt-3 text-sm text-utility-error-700'>{error}</p>}
    </div>
  );
}
